import {deserialize, DrupalNode, DrupalSearchApiFacet} from "next-drupal";
import {DrupalSearchApiResponseMeta} from "@/types/hygiena-types";
import {SearchHero} from "../../organisms/hero/search-hero";
import {createRef, RefObject, useEffect, useRef, useState} from "react";
import {getKvpPair} from "@/lib/get-kvp-pair";
import {useRouter} from "next/router";
import {searchIndex} from "@/lib/search-index";
import {DrupalJsonApiParams} from "drupal-jsonapi-params";
import dynamic from "next/dynamic";

const Divider = dynamic(() => import("../../atoms/divider").then((mod) => mod.Divider));
const Button = dynamic(() => import("../../atoms/button").then((mod) => mod.Button));
const LoadingSpinnerOverlay = dynamic(() => import("../../atoms/loading-spinner").then((mod) => mod.LoadingSpinnerOverlay));
const Pagination = dynamic(() => import("../../organisms/widget--pagination").then((mod) => mod.Pagination));
const PressReleaseTeaser = dynamic(() => import("../nodes/node--press_release").then((mod) => mod.PressReleaseTeaser));


interface PressReleasePageProps {
  node: DrupalNode,
  pressReleases?: DrupalNode[];
  meta?: DrupalSearchApiResponseMeta,
  facets?: DrupalSearchApiFacet[];
}
export function PressReleasesPage({ node, pressReleases, meta, facets: initialFacets }: PressReleasePageProps) {
  const ready = useRef(false);
  const router = useRouter();
  const [facets, setFacets] = useState<DrupalSearchApiFacet[]>(initialFacets ?? []);
  const [dateFilter, setDateFilter] = useState<string|undefined>();
  const grouping = {} as {[key: string]: DrupalNode[]};
  const [documentCount, setDocumentCount] = useState(meta?.count ?? 0);
  const heroHeadline = getKvpPair(node, "hero_headline", "long")?.value;
  const searchPlaceholder = getKvpPair(node, "search_placeholder")?.value;
  const pressReleaseButtonText = getKvpPair(node, "press_release_button_text")?.value ?? "Press Release";
  const attachmentButtonText = getKvpPair(node, "attachments_button_text")?.value ?? "Attachments";
  const [results, setResults] = useState(pressReleases);
  const [pageLimit, setPageLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState<boolean>();
  const scrollRef: RefObject<HTMLDivElement> = createRef();

  if (Array.isArray(results)) {
    results.map(item => {
      const date = new Date(item.field_press_release_date);
      const group = (date.getMonth() + 1) + "/" + date.getFullYear();
      if (!grouping[group]) {
        grouping[group] = [];
      }
      grouping[group].push(item);
    });
  }

  function getParams() {
    const params = new DrupalJsonApiParams()
      .addFilter("language_with_fallback", router?.locale ?? "en")
      .addPageLimit(10)
      .addPageOffset(pageLimit * page)
      .addInclude([
        "field_press_release_attachments",
        "field_press_release_pdf.field_media_file",
      ])
      .addSort("field_press_release_date", "DESC");
    if (searchText) {
      params.addFilter("fulltext", searchText);
    }
    if (dateFilter) {
      params.addFilter("field_press_release_date", `${dateFilter}/01/01`, ">=");
      params.addFilter("field_press_release_date", `${dateFilter}/12/31`, "<=");
    }
    return params.getQueryString();
  }

  function searchCallback(text) {
    setLoading(true);
    setSearchText(text);
  }

  async function updateFacets() {
    let response;
    let results;
    const params = getParams();

    try {
      response = await searchIndex("press_release", params, router.locale, router.defaultLocale);
      response = response.ok ? response.data : {};
      results = response?.data ? await deserialize(response) as DrupalNode[] : [];
    }
    catch (error) {
    }

    setResults(results ?? []);
    await setDocumentCount(response?.meta?.count ?? 0);
    await setFacets(response?.meta?.facets ?? []);
    await setLoading(false);
  }

  async function changePage(page) {
    await setLoading(true);
    await setPage(page);
  }

  useEffect(() => {
    if (!ready?.current) {
      ready.current = true;
      return;
    }
    setLoading(true);
    updateFacets();
  }, [searchText, dateFilter, page, router?.locale]);

  return (
    <>
      <SearchHero title={node?.title} image={node?.field_kvp_hero_image} small={true} headline={heroHeadline} searchPlaceholder={searchPlaceholder} searchCallback={searchCallback}/>
      <div className="container px-6 pb-10 mx-auto">
        <div className="order-first md:order-last md:top-0">
          {facets.map((facet, key) => {
            if (facet?.terms && facet?.terms.length > 1) {
              return (
                <div key={key}>
                  <div className="text-center text-black pb-4 md:pb-12">
                    <div className="max-md:pt-4 text-center flex flex-wrap gap-2 justify-center md:justify-end">
                      {facet.terms.map(term => (
                        <Button
                          key={term.values.value}
                          invert={term.values.value !== dateFilter}
                          className={"font-normal !px-6"}
                          color={term.values.value === dateFilter ? "primary" : "black"}
                          callback={() => {
                            setDateFilter(term.values.value);
                            setPage(0);
                          }}>{term.values.label}</Button>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }
          })}
        </div>
      </div>
      <div className="relative scroll-m-24" ref={scrollRef}>
        <LoadingSpinnerOverlay loading={loading} fixed={true}/>
        {results?.length ? (
          <div className="container px-6 pb-10 mx-auto text-black">
            {Object.keys(grouping).map(key => (
                <div key={key} className="flex flex-wrap flex-col gap-4 mb-16">
                  <h3 className="md:!text-4xl font-light">
                    {new Date(`${key.split("/")[0]}/1/${key.split("/")[1]}`).toLocaleString(router.locale ?? "en-US", {month: "long", year: "numeric"})}
                  </h3>
                  <Divider className="!my-4"/>
                  {grouping[key].map(item => (
                    <PressReleaseTeaser key={item.id} node={item} pressReleaseButtonText={pressReleaseButtonText} attachmentButtonText={attachmentButtonText}/>
                  ))}
                </div>
              ))}
          </div>
        ) : <div className="container mx-auto"><p>No content found.</p></div>}
        {results?.length ? (
          <div className="container mx-auto">
            <Pagination count={documentCount} page={page} limit={pageLimit} changePage={changePage} radius={8} scrollRef={scrollRef}/>
          </div>
        ): <></>}
      </div>
    </>
  );
}
